import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './booking/bookingVars.css';
import './AuthPages.css';
import { siteName } from '../utils/adminSite';
import { PASSWORD_HINT, passwordProblem } from '../utils/passwords';
import { bookingTokens } from '../contexts/BrandingContext';

const SIGNUPS_DISABLED = process.env.REACT_APP_SIGNUPS_DISABLED === 'true';
const HOUSE_POLICIES = 'https://www.trampoline.life/club-policies';

// Trampoline Life's own sign-up page, or, given `club` (from its sign-up link,
// see Join.js), that club's: its name, colour and policies, and the account
// joins it.
const Register = ({ club = null }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', website: '',
  });
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { register, isAuthenticated, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (isAuthenticated) navigate(returnTo || '/dashboard', { replace: true });
  }, [isAuthenticated, navigate, returnTo]);

  const brand = club ? club.name : siteName();
  const policiesUrl = club ? club.policiesUrl : HOUSE_POLICIES;
  const pageStyle = club?.accentColor ? bookingTokens({ secondaryColor: club.accentColor }) : undefined;

  if (SIGNUPS_DISABLED) {
    return (
      <div className="auth-page" style={pageStyle}>
        <div className="auth-card">
          <div className="auth-brand">{brand}</div>
          <h1 className="auth-heading">Sign-ups paused</h1>
          <p className="auth-hint">We're currently migrating our booking system. We'll be accepting new sign-ups again very soon — check back shortly!</p>
          <div className="auth-links">
            <Link to="/login" className="auth-link">Already have an account? Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
    if (validationErrors[name]) setValidationErrors(v => ({ ...v, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'Required';
    if (!formData.lastName.trim()) errors.lastName = 'Required';
    if (!formData.email.trim()) errors.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.phone.trim()) errors.phone = 'Required';
    if (!formData.password) errors.password = 'Required';
    else if (passwordProblem(formData.password)) errors.password = PASSWORD_HINT;
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (policiesUrl && !policiesAccepted) errors.policies = 'You must read and accept the club policies';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setValidationErrors(errors); return; }
    setIsLoading(true);
    setValidationErrors({});
    const result = await register({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      website: formData.website,
      ...(club ? { club: club.link } : {}),
    });
    if (result.success) navigate(returnTo || '/dashboard', { replace: true });
    setIsLoading(false);
  };

  return (
    <div className="auth-page" style={pageStyle}>
      <div className="auth-card">
        {club?.logoUrl && <img src={club.logoUrl} alt="" className="auth-logo" />}
        <div className="auth-brand">{brand}</div>
        <h1 className="auth-heading">Create an account</h1>

        <p className="auth-hint">Please enter <b style={{ color: 'var(--booking-accent)' }}>your</b> details. If you are registering on behalf of children, their details can be added after sign-up.</p>

        {error && <p className="auth-error">{error}</p>}

        <form onSubmit={handleSubmit} className="auth-form">
            {/* Honeypot: hidden from real users, bots fill every field they can find */}
            <label style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              Website
              <input type="text" name="website" tabIndex="-1" autoComplete="off" value={formData.website} onChange={handleChange} />
            </label>
            <label className="auth-label">Your first name <span style={{ color:'var(--booking-accent)', fontSize: '0.7rem' }}><i>Not your child's name</i></span>
              <input name="firstName" value={formData.firstName} onChange={handleChange}
                className={`auth-input${validationErrors.firstName ? ' auth-input--error' : ''}`} required />
              {validationErrors.firstName && <span className="auth-field-error">{validationErrors.firstName}</span>}
            </label>
            <label className="auth-label">Your last name
              <input name="lastName" value={formData.lastName} onChange={handleChange}
                className={`auth-input${validationErrors.lastName ? ' auth-input--error' : ''}`} required />
              {validationErrors.lastName && <span className="auth-field-error">{validationErrors.lastName}</span>}
            </label>

          <label className="auth-label">Email
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              className={`auth-input${validationErrors.email ? ' auth-input--error' : ''}`} required autoComplete="email" />
            {validationErrors.email && <span className="auth-field-error">{validationErrors.email}</span>}
          </label>

          <label className="auth-label">Phone number
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
              className={`auth-input${validationErrors.phone ? ' auth-input--error' : ''}`} required />
            {validationErrors.phone && <span className="auth-field-error">{validationErrors.phone}</span>}
          </label>

          <label className="auth-label">Password
            <input type="password" name="password" value={formData.password} onChange={handleChange}
              className={`auth-input${validationErrors.password ? ' auth-input--error' : ''}`} required autoComplete="new-password" />
            {validationErrors.password
              ? <span className="auth-field-error">{validationErrors.password}</span>
              : <span className="auth-hint" style={{ margin: 0 }}>{PASSWORD_HINT}</span>}
          </label>

          <label className="auth-label">Confirm password
            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
              className={`auth-input${validationErrors.confirmPassword ? ' auth-input--error' : ''}`} required />
            {validationErrors.confirmPassword && <span className="auth-field-error">{validationErrors.confirmPassword}</span>}
          </label>

          {policiesUrl && <>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1.4 }}>
            <input
              type="checkbox"
              checked={policiesAccepted}
              onChange={e => { setPoliciesAccepted(e.target.checked); if (validationErrors.policies) setValidationErrors(v => ({ ...v, policies: '' })); }}
              className="auth-checkbox"
            />
            <span>
              I have read and agree to the{' '}
              <a href={policiesUrl} target="_blank" rel="noopener noreferrer" className="auth-link">
                club policies
              </a>
            </span>
          </label>
          {validationErrors.policies && <span className="auth-field-error">{validationErrors.policies}</span>}
          </>}

          <button type="submit" disabled={isLoading} className="auth-btn auth-btn--primary">
            {isLoading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="auth-links" style={{ flexDirection: 'column', gap: '0.2rem' }}>
          <span>Already have an account?</span>
          <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
